import { isGrpcWebOrConnectContentType } from '@terra/shared-asset-processing';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import { getContentTypeHeader } from './har-body-encoding.utils';
import {
  prepareGrpcHarRequestForPreview,
  prepareGrpcHarResponseForPreview,
} from './grpc-har-body-encoding';
import type { StoredHarDisplayEntry } from './har-view';

const readHarHeaderContentType = (
  headers: Array<{ name: string; value: string }> | undefined,
): string => {
  if (!headers) {
    return '';
  }

  return getContentTypeHeader(
    Object.fromEntries(headers.map(({ name, value }) => [name, value])),
  );
};

type GrpcHarEntryShape = {
  request: unknown;
  response?: unknown | null;
  webGrpcDecodedData?: WebGrpcDecodedData | null;
};

export const isGrpcHarEntry = (harEntry: GrpcHarEntryShape): boolean => {
  const request =
    typeof harEntry.request === 'object' && harEntry.request !== null
      ? (harEntry.request as {
          postData?: { mimeType?: string };
          headers?: Array<{ name: string; value: string }>;
        })
      : null;
  const response =
    typeof harEntry.response === 'object' && harEntry.response !== null
      ? (harEntry.response as {
          content?: { mimeType?: string };
          headers?: Array<{ name: string; value: string }>;
        })
      : null;

  const requestMime =
    request?.postData?.mimeType ?? readHarHeaderContentType(request?.headers);
  const responseMime =
    response?.content?.mimeType ?? readHarHeaderContentType(response?.headers);

  return (
    isGrpcWebOrConnectContentType(requestMime) ||
    isGrpcWebOrConnectContentType(responseMime)
  );
};

export function hasUsableRequestWebGrpcDecode(
  webGrpcDecodedData: WebGrpcDecodedData | null | undefined,
): boolean {
  if (!webGrpcDecodedData) {
    return false;
  }

  if (webGrpcDecodedData.decodedRequest != null) {
    return true;
  }

  return (
    webGrpcDecodedData.status === 'PARTIAL' && Boolean(webGrpcDecodedData.error)
  );
}

export function hasUsableResponseWebGrpcDecode(
  webGrpcDecodedData: WebGrpcDecodedData | null | undefined,
): boolean {
  if (!webGrpcDecodedData) {
    return false;
  }

  if (webGrpcDecodedData.decodedResponse != null) {
    return true;
  }

  return (
    webGrpcDecodedData.status === 'PARTIAL' && Boolean(webGrpcDecodedData.error)
  );
}

export function mergeWebGrpcDecodedData({
  base,
  overlay,
}: {
  base?: WebGrpcDecodedData | null;
  overlay?: WebGrpcDecodedData | null;
}): WebGrpcDecodedData | null {
  if (!base && !overlay) {
    return null;
  }

  if (!overlay) {
    return base ?? null;
  }

  if (!base) {
    return overlay;
  }

  return {
    ...base,
    ...overlay,
    decodedRequest: overlay.decodedRequest ?? base.decodedRequest,
    decodedResponse: overlay.decodedResponse ?? base.decodedResponse,
  };
}

export function isGrpcHarDecodeComplete(
  webGrpcDecodedData: WebGrpcDecodedData | null | undefined,
): boolean {
  return (
    hasUsableRequestWebGrpcDecode(webGrpcDecodedData) &&
    hasUsableResponseWebGrpcDecode(webGrpcDecodedData)
  );
}

export function hasAnyUsableGrpcHarDecode(
  webGrpcDecodedData: WebGrpcDecodedData | null | undefined,
): boolean {
  return (
    hasUsableRequestWebGrpcDecode(webGrpcDecodedData) ||
    hasUsableResponseWebGrpcDecode(webGrpcDecodedData)
  );
}

const prepareStoredHarRequestForGrpcPreview = (
  request: StoredHarDisplayEntry['request'],
): StoredHarDisplayEntry['request'] => {
  if (
    typeof request !== 'object' ||
    request === null ||
    Array.isArray(request)
  ) {
    return request;
  }

  return prepareGrpcHarRequestForPreview(
    request as Parameters<typeof prepareGrpcHarRequestForPreview>[0],
  ) as StoredHarDisplayEntry['request'];
};

const prepareStoredHarResponseForGrpcPreview = (
  response: StoredHarDisplayEntry['response'],
): StoredHarDisplayEntry['response'] => {
  if (
    typeof response !== 'object' ||
    response === null ||
    Array.isArray(response)
  ) {
    return response;
  }

  return prepareGrpcHarResponseForPreview(
    response as Parameters<typeof prepareGrpcHarResponseForPreview>[0],
  ) as StoredHarDisplayEntry['response'];
};

export const resolveGrpcHarPreviewResponse = (
  response: StoredHarDisplayEntry['response'],
): StoredHarDisplayEntry['response'] =>
  prepareStoredHarResponseForGrpcPreview(
    response ?? { status: 0, content: { text: '' } },
  );

export const buildGrpcHarPreviewDecodePayload = ({
  applicationId,
  harEntry,
}: {
  applicationId: string;
  harEntry: StoredHarDisplayEntry;
}) => ({
  applicationId,
  request: prepareStoredHarRequestForGrpcPreview(harEntry.request),
  response: resolveGrpcHarPreviewResponse(harEntry.response),
});

export type ToolHarEnrichmentInput = {
  applicationId?: string | null;
  harEntry: StoredHarDisplayEntry;
};

const toStoredHarDisplayEntry = ({
  har,
}: {
  har: ToolHarEnrichmentInput;
}): StoredHarDisplayEntry => ({
  request: har.harEntry.request,
  response: har.harEntry.response,
  webGrpcDecodedData: har.harEntry.webGrpcDecodedData ?? null,
});

export async function enrichToolHarEntryForDisplay({
  har,
  decodeGrpcHarPreview,
  isGrpcWebSupportEnabled = false,
}: {
  har: ToolHarEnrichmentInput;
  decodeGrpcHarPreview: (payload: {
    applicationId: string;
    request: StoredHarDisplayEntry['request'];
    response: StoredHarDisplayEntry['response'];
  }) => Promise<WebGrpcDecodedData | null>;
  isGrpcWebSupportEnabled?: boolean;
}): Promise<StoredHarDisplayEntry> {
  const baseEntry = toStoredHarDisplayEntry({ har });

  if (
    !isGrpcWebSupportEnabled ||
    !isGrpcHarEntry(baseEntry) ||
    !har.applicationId ||
    isGrpcHarDecodeComplete(baseEntry.webGrpcDecodedData)
  ) {
    return baseEntry;
  }

  try {
    const overlay = await decodeGrpcHarPreview(
      buildGrpcHarPreviewDecodePayload({
        applicationId: har.applicationId,
        harEntry: baseEntry,
      }),
    );

    if (!overlay) {
      return baseEntry;
    }

    const mergedDecode = mergeWebGrpcDecodedData({
      base: baseEntry.webGrpcDecodedData,
      overlay,
    });

    if (!hasAnyUsableGrpcHarDecode(mergedDecode)) {
      return baseEntry;
    }

    return {
      ...baseEntry,
      webGrpcDecodedData: mergedDecode,
    };
  } catch {
    return baseEntry;
  }
}
